/**
 * خدمة إدارة المسودات المحلية
 * تتعامل مع حفظ واسترجاع بيانات النموذج من التخزين المحلي
 */

import { saveSelectedFiles, loadSelectedFiles, clearSelectedFiles } from './fileStorageService';

const DRAFT_STORAGE_KEY = 'nominationFormData';
const CURRENT_DATA_VERSION = '1.0';

/**
 * التحقق من توفر التخزين المحلي
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * حفظ المسودة في التخزين المحلي
 * @param {Object} formData - بيانات النموذج
 * @param {Object} options - خيارات إضافية
 */
export const saveDraft = async (formData, options = {}) => {
  if (!isLocalStorageAvailable()) {
    throw new Error('التخزين المحلي غير متوفر');
  }

  try {
    const draftData = {
      version: CURRENT_DATA_VERSION,
      ...formData,
      lastSaved: new Date().toISOString(),
      ...options
    };

    // لا نحفظ الملفات في المسودة لتجنب المشاكل الأمنية
    // سيتم إجبار المستخدم على اختيار الملفات مرة أخرى عند استرجاع المسودة
    if (formData.formData && formData.formData.documents) {
      // إزالة بيانات الملفات من المسودة
      draftData.formData = {
        ...draftData.formData,
        documents: {} // إعادة تعيين بيانات الملفات لكائن فارغ
      };
      draftData.hasFiles = false;
      draftData.filesCount = 0;
    }

    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
    
    return {
      success: true,
      message: 'تم حفظ المسودة بنجاح',
      lastSaved: draftData.lastSaved
    };
  } catch (error) {
    console.error('خطأ في حفظ المسودة:', error);
    throw new Error('فشل في حفظ المسودة: ' + error.message);
  }
};

/**
 * استرجاع المسودة من التخزين المحلي
 */
export const loadDraft = async () => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const savedData = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!savedData) {
      return null;
    }

    const parsedData = JSON.parse(savedData);
    
    // التحقق من إصدار البيانات
    if (parsedData.version !== CURRENT_DATA_VERSION) {
      console.warn('إصدار البيانات المحفوظة قديم، سيتم تجاهلها');
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      clearSelectedFiles(); // حذف الملفات المرتبطة
      return null;
    }

    // لا نسترجع الملفات من المسودة لتجنب المشاكل الأمنية
    // سيتم إجبار المستخدم على اختيار الملفات مرة أخرى
    if (parsedData.formData) {
      parsedData.formData.documents = {}; // إعادة تعيين بيانات الملفات
    }

    return parsedData;
  } catch (error) {
    console.error('خطأ في تحميل المسودة:', error);
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    clearSelectedFiles();
    return null;
  }
};

/**
 * حذف المسودة من التخزين المحلي
 */
export const deleteDraft = () => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    clearSelectedFiles(); // حذف الملفات المرتبطة
    return true;
  } catch (error) {
    console.error('خطأ في حذف المسودة:', error);
    return false;
  }
};

/**
 * التحقق من وجود مسودة محفوظة
 */
export const hasDraft = () => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    const savedData = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!savedData) {
      return false;
    }

    const parsedData = JSON.parse(savedData);
    return parsedData.version === CURRENT_DATA_VERSION;
  } catch (error) {
    return false;
  }
};

/**
 * الحصول على معلومات المسودة (تاريخ الحفظ، إلخ)
 */
export const getDraftInfo = async () => {
  const draft = await loadDraft();
  if (!draft) {
    return null;
  }

  return {
    lastSaved: draft.lastSaved,
    currentStep: draft.currentStep,
    hasPersonalInfo: Object.keys(draft.formData?.personalInfo || {}).length > 0,
    hasDocuments: false, // لا نحفظ بيانات الملفات في المسودة
    hasNomination: Object.keys(draft.formData?.nomination || {}).length > 0,
    hasFiles: false, // لا نحفظ الملفات في المسودة
    filesCount: 0,
    version: draft.version
  };
};

/**
 * تنظيف البيانات القديمة أو التالفة
 */
export const cleanupOldDrafts = () => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const savedData = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!savedData) {
      return;
    }

    const parsedData = JSON.parse(savedData);
    
    // حذف البيانات إذا كانت قديمة (أكثر من 30 يوم)
    if (parsedData.lastSaved) {
      const lastSavedDate = new Date(parsedData.lastSaved);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      if (lastSavedDate < thirtyDaysAgo) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        clearSelectedFiles(); // حذف الملفات المرتبطة
        console.log('تم حذف المسودة القديمة');
      }
    }
  } catch (error) {
    console.error('خطأ في تنظيف المسودات القديمة:', error);
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    clearSelectedFiles();
  }
};

/**
 * تصدير البيانات كملف JSON
 */
export const exportDraft = () => {
  const draft = loadDraft();
  if (!draft) {
    throw new Error('لا توجد مسودة للتصدير');
  }

  const dataStr = JSON.stringify(draft, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `nomination-draft-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * استيراد البيانات من ملف JSON
 */
export const importDraft = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        
        // التحقق من صحة البيانات
        if (!importedData.version || !importedData.formData) {
          reject(new Error('ملف البيانات غير صحيح'));
          return;
        }
        
        // حفظ البيانات المستوردة
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
          ...importedData,
          lastSaved: new Date().toISOString()
        }));
        
        resolve(importedData);
      } catch (error) {
        reject(new Error('فشل في قراءة الملف: ' + error.message));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('فشل في قراءة الملف'));
    };
    
    reader.readAsText(file);
  });
};
